import { useEffect, useRef, useState } from "react";
import type { NftStatus, NftView } from "../types";
import { HourglassIcon, LeafIcon } from "../../../components/Icons";
import { useArtworkLoad } from "../state/use-artwork-load";

export function nftStateLabel(status: NftStatus): string {
  return { PENDING_RANDOMNESS: "Waiting for randomness", RANDOMNESS_READY: "Ready to finalize",
    FINALIZED: "Reward finalized", BURNED: "Redeemed" }[status];
}

export function nftMediaLabel(nft: NftView): string {
  if (nft.imageUrl && nft.imageKind === "CANONICAL_TOKEN_URI") return nft.imageUrl.startsWith("https:") ?
    "Validated tokenURI · PNG hosted by backend" : "Verified tokenURI artwork";
  if (nft.imageUrl && nft.imageKind === "DEMO_PREVIEW") return "Demo artwork · not tokenURI";
  return nft.imageKind === "PENDING" ? "Waiting · artwork not available yet" : "Canonical artwork unavailable";
}

/** Consumes already validated view media; canonical validation remains in the mapper. */
export function NftArtwork({ nft, lazy = false, onMetadataMismatch }: {
  nft: NftView; lazy?: boolean; onMetadataMismatch?: (() => void) | undefined;
}) {
  if (nft.imageUrl && (nft.imageKind === "CANONICAL_TOKEN_URI" || nft.imageKind === "DEMO_PREVIEW")) {
    return <LoadableNftArtwork key={JSON.stringify([nft.collectionAddress, nft.tokenId, nft.owner, nft.sourceToken, nft.imageKind, nft.imageUrl])}
      nft={nft} imageUrl={nft.imageUrl} lazy={lazy} onMetadataMismatch={onMetadataMismatch} />;
  }
  return nft.imageKind === "PENDING" ? <div className="pending-relic" role="img"
    aria-label={`NFT ${nft.tokenId}: ${nftStateLabel(nft.status).toLowerCase()}; artwork not available yet`}>
    <div className="pending-rings"><i /><i /><i /></div><HourglassIcon /><span>Artwork pending</span>
  </div> : <div className="image-unavailable"><LeafIcon /><span>Canonical artwork unavailable</span></div>;
}

function LoadableNftArtwork({ nft, imageUrl, lazy, onMetadataMismatch }: {
  nft: NftView; imageUrl: string; lazy: boolean; onMetadataMismatch?: (() => void) | undefined;
}) {
  const load = useArtworkLoad(imageUrl, onMetadataMismatch);
  if (load.failed) return <div className="image-unavailable" role="status"><LeafIcon />
    <span>{load.changed ? "Artwork changed. Refresh NFT details." : load.retrying ? "Artwork unavailable. Retrying…" : "Artwork unavailable. Try refreshing later."}</span>
  </div>;
  return <DecodedNftImage key={load.attempt} imageUrl={imageUrl} lazy={lazy} onError={load.onError}
    alt={nft.imageKind === "DEMO_PREVIEW" ? `Demo artwork for NFT ${nft.tokenId}; not tokenURI media` :
      imageUrl.startsWith("https:") ? `NFT ${nft.tokenId}; validated tokenURI, PNG hosted by backend; ${nftStateLabel(nft.status).toLowerCase()}` :
      `Verified tokenURI artwork for NFT ${nft.tokenId}; ${nftStateLabel(nft.status).toLowerCase()}`} />;
}

function DecodedNftImage({ imageUrl, lazy, alt, onError }: {
  imageUrl: string; lazy: boolean; alt: string; onError: () => void;
}) {
  const imageRef = useRef<HTMLImageElement>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const image = imageRef.current;
    if (!image) return;
    let active = true;
    let decoding = false;
    const reveal = () => {
      if (decoding) return;
      decoding = true;
      // Keep progressive/partially decoded artwork behind the skeleton.
      const decoded = typeof image.decode === "function" ? image.decode() : Promise.resolve();
      decoded.then(() => { if (active) setReady(true); }).catch(() => { if (active) onError(); });
    };
    image.addEventListener("load", reveal);
    // Cached images may have finished before the listener was attached.
    if (image.complete && image.naturalWidth > 0) reveal();
    return () => { active = false; image.removeEventListener("load", reveal); };
  }, [imageUrl, onError]);

  return <div className="nft-artwork" aria-busy={!ready}>
    <img ref={imageRef} className={`nft-artwork-image${ready ? " is-ready" : ""}`} src={imageUrl}
      loading={lazy ? "lazy" : undefined} decoding="async" onError={onError} alt={alt} />
    {!ready && <div className="nft-artwork-skeleton" aria-hidden="true"><LeafIcon /></div>}
  </div>;
}
